import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Drives every interactive behavior on the AInO landing page: the scrolled
 * nav state, the scroll-linked fabric-thickness animation, the feel/category
 * toggles, the quick-view dialogs, and redirecting straight to checkout for
 * "Mua Ngay". Real cart mutations are left entirely to the theme's existing
 * `product-form-component` / `cart-drawer-component` (see the `<form>`s
 * rendered inside each quick-view dialog) — this component never talks to
 * `/cart/add.js` itself.
 *
 * @extends {Component}
 */
class AinoLandingComponent extends Component {
  #pendingBuyNow = false;

  /**
   * The theme scrolls `.page-wrapper` itself (not `window`) at desktop widths — see the
   * `overflow-y: auto` rule scoped to `min-width: 990px` in base.css. Below that breakpoint
   * `.page-wrapper` reverts to normal flow and `window` scrolls instead. Listening on both
   * covers whichever one is actually the active scrolling container at the current width.
   *
   * @type {Element | null}
   */
  #pageWrapper = null;

  connectedCallback() {
    super.connectedCallback();
    document.documentElement.classList.add('aino-landing-page');
    this.#pageWrapper = document.querySelector('.page-wrapper');
    this.#onScroll();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#updateNavHeight, { passive: true });
    this.#pageWrapper?.addEventListener('scroll', this.#onScroll, { passive: true });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.documentElement.classList.remove('aino-landing-page');
    document.documentElement.style.removeProperty('--aino-nav-height');
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#updateNavHeight);
    this.#pageWrapper?.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
  }

  /**
   * Toggles the "Cảm Nhận" comparison card between the regular-underwear copy and the AInO copy.
   *
   * @param {string} mode - 'regular' or 'aino'.
   */
  setFeel = (mode) => {
    if (!this.refs.feelRoot) return;
    this.refs.feelRoot.dataset.feel = mode;
  };

  /**
   * Toggles the "Danh Mục" section between Cạp Cao and Cạp Vừa.
   *
   * @param {string} mode - 'cao' or 'vua'.
   */
  setCategory = (mode) => {
    if (!this.refs.catRoot) return;
    this.refs.catRoot.dataset.category = mode;
  };

  /**
   * Opens the quick-view dialog matching the current category selection when its image is clicked.
   */
  openCurrentCategoryDialog = () => {
    const mode = this.refs.catRoot?.dataset.category;
    const id = mode === 'vua' ? 'quickview-cap-vua' : 'quickview-cap-cao';
    this.openDialog(id);
  };

  /**
   * Scrolls a nav link's target section into view instead of letting the browser jump to it.
   *
   * The native hash jump parks the section's top edge at the top of the viewport, which on a
   * laptop-height window leaves the tall product photo in "Danh Mục" clipped off the bottom —
   * the visitor has to scroll again to actually see the product they just asked for. When the
   * section marks a `[data-anchor-focus]` element, scroll far enough that that element ends up
   * fully inside the viewport, stopping short of hiding the section's heading behind the nav.
   *
   * @param {Event} event
   */
  scrollToSection = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href^="#"]') : null;
    const id = link?.getAttribute('href')?.slice(1);
    const section = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    if (!(section instanceof HTMLElement)) return;

    event.preventDefault();

    // See the `#pageWrapper` note above: `.page-wrapper` is the element that actually scrolls at
    // desktop widths, and `window` is the one that scrolls below 990px.
    const wrapper = this.#pageWrapper;
    const wrapperScrolls =
      wrapper instanceof HTMLElement && ['auto', 'scroll'].includes(getComputedStyle(wrapper).overflowY);
    const scroller = wrapperScrolls ? /** @type {HTMLElement} */ (wrapper) : window;
    const scrollTop = wrapperScrolls ? /** @type {HTMLElement} */ (wrapper).scrollTop : window.scrollY;
    const viewportHeight = wrapperScrolls ? /** @type {HTMLElement} */ (wrapper).clientHeight : window.innerHeight;

    const sectionTop = scrollTop + section.getBoundingClientRect().top;
    const focus = section.querySelector('[data-anchor-focus]') ?? section;
    const focusRect = focus.getBoundingClientRect();
    const focusBottom = scrollTop + focusRect.bottom;
    // The heading has to stay readable no matter how far we scroll past the section's top padding.
    const guard = section.querySelector('.aino-heading') ?? focus;
    const guardTop = scrollTop + guard.getBoundingClientRect().top;

    const navHeight = this.#scrolledNavHeight();
    const bottomGap = 24;
    const guardGap = 8;

    // Land the section's top edge just below the nav — but if the focus element would still hang
    // off the bottom there, keep scrolling until it clears the fold. The guard caps how far that
    // can go: on a short viewport where the two can't both fit, the heading wins.
    const target = Math.min(
      Math.max(sectionTop - navHeight, focusBottom + bottomGap - viewportHeight),
      guardTop - navHeight - guardGap
    );

    scroller.scrollTo({ top: Math.max(0, target) });
    history.replaceState(null, '', `#${id}`);
  };

  /**
   * The height the nav will have *after* the scroll, not the one it has right now: it shrinks by
   * ~24px as soon as the page leaves the top (`.is-scrolled`), and every target `scrollToSection`
   * computes is far past that threshold. Measuring the expanded height instead would leave the
   * section short by exactly that much. The transition is suppressed so the forced reflow reports
   * the settled height rather than an in-flight animated one, and the class is put back the way it
   * was — `#onScroll` re-applies it, with its animation intact, on the way down.
   *
   * @returns {number}
   */
  #scrolledNavHeight() {
    const nav = this.refs.navRoot;
    if (!(nav instanceof HTMLElement)) return 0;

    const wasScrolled = nav.classList.contains('is-scrolled');
    nav.style.transition = 'none';
    nav.classList.add('is-scrolled');
    const height = nav.getBoundingClientRect().height;
    nav.classList.toggle('is-scrolled', wasScrolled);
    nav.style.removeProperty('transition');

    return height;
  }

  /**
   * @param {string} id - The id of the `<dialog>` to open.
   */
  openDialog = (id) => {
    const dialog = this.querySelector(`#${id}`);
    if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
  };

  /**
   * @param {Event} event
   */
  closeDialog = (event) => {
    const dialog = event.target instanceof Element ? event.target.closest('dialog') : null;
    dialog?.close();
  };

  /**
   * Closes a dialog when the click lands on its backdrop area (the `<dialog>` element itself,
   * not its inner panel — a click that hits the panel or its children has a different `target`).
   *
   * @param {Event} event
   */
  closeDialogOnBackdrop = (event) => {
    if (event.target instanceof HTMLDialogElement) event.target.close();
  };

  /**
   * Swaps the "1 Chiếc" quick-view photo to match the clicked color swatch. Only wired up in
   * placeholder mode (no real product linked yet) — once a product with color variants is
   * linked, the swatches submit real variant ids instead (see the `color_option` branch in
   * aino-quick-view-dialog.liquid).
   *
   * @param {Event} event
   */
  previewChiecColor = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(button instanceof HTMLElement)) return;
    const media = button.closest('.aino-dialog__panel')?.querySelector('.aino-dialog__media-img');
    if (button.dataset.image && media instanceof HTMLImageElement) media.src = button.dataset.image;
    for (const swatch of button.parentElement?.children ?? []) {
      swatch.classList.toggle('is-active', swatch === button);
    }
  };

  /**
   * @param {Event} event
   */
  incQty = (event) => this.#stepQty(event, 1);

  /**
   * @param {Event} event
   */
  decQty = (event) => this.#stepQty(event, -1);

  /**
   * @param {Event} event
   * @param {number} delta
   */
  #stepQty(event, delta) {
    const dialog = event.target instanceof Element ? event.target.closest('dialog') : null;
    const input = dialog?.querySelector('[data-qty-input]');
    const display = dialog?.querySelector('[data-qty-display]');
    if (!(input instanceof HTMLInputElement) || !display) return;
    const next = Math.min(9, Math.max(1, Number(input.value || '1') + delta));
    input.value = String(next);
    display.textContent = String(next);
  }

  /**
   * Marks the form submission that's about to happen as a "buy now" — handled once the
   * resulting `CartLinesUpdateEvent` promise resolves in `#onCartLinesUpdate`.
   */
  markBuyNow = () => {
    this.#pendingBuyNow = true;
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    const buyNow = this.#pendingBuyNow;
    this.#pendingBuyNow = false;

    // Neither submit button disables itself while the request is in flight, so a dialog with no
    // other feedback invites repeat clicks — each one fires a real new /cart/add.js request and a
    // few of those in quick succession is enough to trip Shopify's cart rate limit. Lock both
    // buttons for the duration of this request so a slow response can't be mistaken for "nothing
    // happened" and clicked again.
    const dialog = event.target instanceof Element ? event.target.closest('dialog.aino-dialog') : null;
    const submitButtons = dialog?.querySelectorAll('button[type="submit"]') ?? [];
    for (const button of submitButtons) button.disabled = true;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        if (buyNow) {
          window.location.href = `${Theme.routes.cart_url}`.replace(/\.json$/, '') + '/checkout';
          return;
        }
        // "Thêm Vào Giỏ" leaves the quick-view dialog open, but the theme's cart drawer only
        // auto-opens once any modal it was triggered from has closed (see cart-drawer.js) — so
        // without this, the dialog would just sit there with no visible confirmation. Close it
        // the same way the theme's own quick-add dialog does after a successful add.
        if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
      })
      .catch(() => {})
      .finally(() => {
        for (const button of submitButtons) button.disabled = false;
      });
  };

  #onScroll = () => {
    const scrollY = window.scrollY || this.#pageWrapper?.scrollTop || 0;
    if (this.refs.navRoot) this.refs.navRoot.classList.toggle('is-scrolled', scrollY > 40);
    this.#updateNavHeight();
    this.#updateFabricProgress();
  };

  #updateNavHeight = () => {
    const nav = this.refs.navRoot;
    if (!(nav instanceof HTMLElement)) return;

    document.documentElement.style.setProperty('--aino-nav-height', `${Math.ceil(nav.getBoundingClientRect().height)}px`);
  };

  #updateFabricProgress() {
    const section = this.refs.fabricSection;
    if (!(section instanceof HTMLElement)) return;

    const rect = section.getBoundingClientRect();
    const vh = window.innerHeight;
    const navOffset = 100;
    const total = rect.height - vh - navOffset;
    let progress = total > 0 ? (-rect.top - navOffset) / total : 0;
    progress = Math.max(0, Math.min(1, progress));

    const startMM = 1.0;
    const midMM = 0.5;
    const endMM = 0.16;
    const midPoint = 0.3;
    const currentMM =
      progress <= midPoint
        ? startMM + (midMM - startMM) * (progress / midPoint)
        : midMM + (endMM - midMM) * ((progress - midPoint) / (1 - midPoint));
    const thicknessLabel = progress > 0.96 ? '0.16mm' : `${currentMM.toFixed(2)}mm`;

    const startPx = 26;
    const endPx = 2;
    const threadPx = startPx + (endPx - startPx) * progress;

    const lerp = (a, b, t) => Math.round(a + (b - a) * t);
    const from = [217, 217, 217];
    const to = [169, 192, 240];
    const threadColor = `rgb(${lerp(from[0], to[0], progress)}, ${lerp(from[1], to[1], progress)}, ${lerp(from[2], to[2], progress)})`;

    const ainoBarScale = 0.7 + 0.3 * progress;
    const ainoBarOpacity = 0.5 + 0.5 * progress;

    if (this.refs.thicknessLabel) this.refs.thicknessLabel.textContent = thicknessLabel;
    if (this.refs.threadBar instanceof HTMLElement) {
      this.refs.threadBar.style.background = threadColor;
      this.refs.threadBar.style.transform = `scaleX(${threadPx / 2})`;
    }
    if (this.refs.ainoBar instanceof HTMLElement) {
      this.refs.ainoBar.style.transform = `scaleY(${ainoBarScale})`;
      this.refs.ainoBar.style.opacity = String(ainoBarOpacity);
    }
  }
}

if (!customElements.get('aino-landing-component')) {
  customElements.define('aino-landing-component', AinoLandingComponent);
}
